import React, { useMemo } from "react";
import "./invoice-template.style.scss";
import logoDark from "../../assets/logo-dark-BILLME.png";

// for date purpose
import moment from "moment";
import {
	converToWord,
	fromAdToBs,
	isEmptyObject,
	rupeeConverter,
} from "../../global/function";

import { useSession } from "../../queries/auth.queries";
import { useCompanyDetails } from "../../queries/company.queries";
import type {
	Company,
	CompanySnapshotData,
} from "../../queries/company.queries";

// Scoped to the fields this component actually reads. Mirrors
// billing_system_BE/models/bill.js + models/creditNote.js (credit-note fields
// are flattened onto the bill object by the caller, e.g.
// invoice-bill.page.jsx's _renderTemplate spreads `...selectedBill.credit_note`
// directly rather than nesting it). The full Bill/Draft type belongs to the
// future bill.queries.ts migration (Batch 5-C), not this print-only view.
export interface BilledItem {
	item_name?: string;
	hs_code?: string;
	item_price?: number | string;
	item_qty?: number | string;
	total_price?: number;
	vatable?: boolean;
}

export interface InvoiceTemplateBill {
	bill_id?: string;
	draft_id?: string;
	invoice_number?: string;
	invoice_date?: string;
	sales_date?: string;
	buyer_name?: string;
	buyer_pan?: string;
	customer_mobile?: string;
	customer_email?: string;
	bill_for?: string;
	print_copies?: number;
	bill_void?: boolean;
	bill_canceled?: boolean;
	ird_bill?: "pending" | "success" | "failed";
	sub_total?: number;
	total_sales?: number;
	payment_mode?: string;
	non_taxable_amt?: number;
	taxable_sales_vat?: number;
	non_taxable_discount?: number;
	taxable_discount?: number;
	non_taxable_service_charge?: number;
	taxable_service_charge?: number;
	vat?: number;
	amount_for_esf?: number;
	esf?: number;
	sale_info?: string;
	billed_items?: BilledItem[];
	sliced_items?: BilledItem[];
	company?: Partial<Company>;
	// company_snapshots row this bill was created under — read exclusively
	// (never merged with live `company` data) whenever present. See
	// hasSnapshot/snapshot below.
	company_snapshot?: { data: CompanySnapshotData } | null;
	company_user?: { user?: { user_id?: number } };
	// Credit-note fields, flattened onto the bill object by the caller.
	credit_note_number?: string;
	ref_invoice_number?: string;
	credit_note_date?: string;
	reason_for_return?: string;
}

interface InvoiceTemplateProps {
	selectedBill?: InvoiceTemplateBill;
	showBillFooter?: boolean;
	page?: string;
	fontStyle?: React.CSSProperties;
	isCreditNote?: boolean;
	lastCount?: number;
}

export default function InvoiceTemplate({
	selectedBill,
	showBillFooter,
	page,
	fontStyle,
	isCreditNote = false,
	lastCount = 0,
}: InvoiceTemplateProps) {
	// registration_number and terms_condition have no per-bill column — for a
	// bill with a company_snapshot they come from the snapshot (accurate as of
	// bill-creation time); for drafts and pre-snapshot bills they fall back to
	// the live company query, same as before this snapshot support existed.
	const { data: companyData } = useCompanyDetails();
	const liveTermsCondition = companyData?.company?.preference?.terms_condition;
	const liveRegistrationNumber =
		companyData?.company?.identification?.registration_number;
	const { data: session } = useSession();

	// Print-time company data source: a bill's own company_snapshot (frozen at
	// creation time) whenever it has one, never blended with live data — a
	// `null` field inside a real snapshot must stay `null`, not silently fall
	// back to the live company (hence a hasSnapshot ternary throughout this
	// file, not `??`). Drafts and any pre-snapshot bill fall back to the live
	// `company` association / the shared company query, unchanged from before.
	const snapshot = selectedBill?.company_snapshot?.data ?? null;
	const hasSnapshot = selectedBill?.company_snapshot != null;

	const companySource = hasSnapshot ? snapshot?.company : selectedBill?.company;
	const company_logo = companySource?.company_logo;
	const company_name = companySource?.company_name;
	const company_color_code = companySource?.company_color_code;
	const company_phone = companySource?.company_phone;
	const company_mobile = companySource?.company_mobile;
	const company_email = companySource?.company_email;
	const company_address = companySource?.company_address;
	const company_city = companySource?.company_city;
	const company_country = companySource?.company_country;
	const company_website = companySource?.company_website;

	const termsCondition = hasSnapshot
		? (snapshot?.preference?.terms_condition ?? null)
		: liveTermsCondition;
	const registrationNumber = hasSnapshot
		? (snapshot?.identification?.registration_number ?? null)
		: liveRegistrationNumber;

	const getBillDate = (date?: string) => {
		const billNepDate = fromAdToBs(date, "DD MMMM, YYYY");
		const billEnDate = moment(date).format("DD MMMM, YYYY");
		return { billNepDate, billEnDate };
	};

	const TotalAmtInWords = useMemo(
		() => converToWord(selectedBill?.total_sales?.toFixed(2) || 0),
		[selectedBill?.total_sales],
	);

	// VAT and ESF are either/or (see billing-charge.util.ts) and, unlike the
	// live company preference, `selectedBill`'s own stored amounts reflect
	// whichever mode was actually active when this specific bill was created
	// — correct even for a reprint after the company later switches modes.
	const vatEnabled = useMemo(
		() => (selectedBill?.vat ?? 0) > 0,
		[selectedBill],
	);
	const esfEnabled = useMemo(
		() => (selectedBill?.esf ?? 0) > 0,
		[selectedBill],
	);
	const serviceChargeEnabled = useMemo(
		() =>
			(selectedBill?.taxable_service_charge ?? 0) > 0 ||
			(selectedBill?.non_taxable_service_charge ?? 0) > 0,
		[selectedBill],
	);
	// ESF's rate isn't stored on the bill (only the resulting base/charge
	// amounts are), so it's derived back from them rather than read off the
	// live company preference — keeps the label historically accurate too.
	const esfRatePercent = useMemo(() => {
		if (!selectedBill?.amount_for_esf) return 0;
		return Number(
			(
				((selectedBill?.esf ?? 0) / selectedBill.amount_for_esf) *
				100
			).toFixed(2),
		);
	}, [selectedBill]);

	const creditNoteDate = useMemo(
		() =>
			selectedBill?.credit_note_date
				? getBillDate(selectedBill.credit_note_date)
				: null,
		[selectedBill?.credit_note_date],
	);

	function _renderInvoiceDate(invDate?: string, label = "Invoice Date") {
		const billDate = invDate ? getBillDate(invDate) : null;
		return (
			<div className="text-section">
				<div className="dark-text">
					<b>{label}:</b>
				</div>
				<div className="light-text text-col">
					{billDate && (
						<>
							<span>{billDate.billNepDate}</span>
							<span>{billDate.billEnDate}</span>
						</>
					)}
				</div>
			</div>
		);
	}

	function _renderBillFor(billForStr: string) {
		return (
			<div className="tags-wrapper" style={{ padding: 0 }}>
				{billForStr
					.split("#")
					.filter(Boolean)
					.map((tag, index) => (
						<div className="tag-container" key={index}>
							<span className="tag-item">{tag}</span>
						</div>
					))}
			</div>
		);
	}
	return (
		<div className="template-wrapper" style={fontStyle}>
			<div className="template-container">
				<div className="template-header-container">
					<div className="header-row-wrapper">
						<div className="header-row-item">
							<div className="brand-logo-container">
								<img
									crossOrigin="anonymous"
									src={company_logo || logoDark}
									alt="logo"
									className="brand-image"
								/>
								<div className="brand-text-container">
									<div
										className="brand-text brand-title"
										style={{
											color: company_color_code,
										}}
									>
										{company_name || "Company Name"}
									</div>
									{!isEmptyObject(companySource) && (
										<div className="brand-text">
											<b>VAT / PAN No.: </b>
											{registrationNumber}
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="header-row-item">
							{isCreditNote ? (
								<div className="invoice-container">
									<div className="invoice-title credit">
										Credit Note
									</div>
									<div className="invoice-text-container">
										<div className="text-section">
											<div className="dark-text">
												<b>Credit Note #:</b>
											</div>
											<div className="light-text">
												{
													selectedBill?.credit_note_number
												}
											</div>
										</div>

										<div className="text-section">
											<div className="dark-text">
												<b>Reference Invoice #:</b>
											</div>
											<div className="light-text">
												{
													selectedBill?.ref_invoice_number
												}
											</div>
										</div>
										<div className="text-section">
											<div className="dark-text">
												<b>Credit Note Date:</b>
											</div>
											<div className="light-text text-col">
												{creditNoteDate && (
													<>
														<span>
															{
																creditNoteDate.billNepDate
															}
														</span>
														<span>
															{
																creditNoteDate.billEnDate
															}
														</span>
													</>
												)}
											</div>
										</div>
										<div className="text-section">
											<div className="dark-text">
												<b>Page:</b>
											</div>
											<div className="light-text">
												{page}
											</div>
										</div>
									</div>
								</div>
							) : (
								<div className="invoice-container">
									{selectedBill?.draft_id ? (
										<div className="invoice-title draft">
											Quotation
										</div>
									) : (
										<div className="invoice-title bill">
											{selectedBill?.print_copies &&
											selectedBill.print_copies > 0
												? "Invoice"
												: "Tax Invoice"}
										</div>
									)}

									<div className="invoice-text-container">
										<div className="text-section">
											<div className="dark-text">
												<b>Invoice #:</b>
											</div>
											<div className="light-text">
												{selectedBill?.invoice_number}
											</div>
										</div>
										{_renderInvoiceDate(
											selectedBill?.invoice_date,
										)}
										{_renderInvoiceDate(
											selectedBill?.sales_date,
											"Sales Date",
										)}

										<div className="text-section">
											<div className="dark-text">
												<b>Page:</b>
											</div>
											<div className="light-text">
												{page}
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
					<div className="header-col-wrapper">
						<div className="header-row-wrapper">
							<div className="bill-container">
								<div className="text-container">
									<h5 className="section-title">
										Invoice To:
									</h5>
								</div>
								<div className="text-section">
									<div className="dark-text">Name:</div>
									<div className="light-text cap-display">
										{selectedBill?.buyer_name}
									</div>
								</div>
								<div className="text-section">
									<div className="dark-text">Contact:</div>
									<div className="light-text">
										{selectedBill?.customer_mobile &&
											selectedBill?.customer_mobile}
									</div>
								</div>

								<div className="text-section">
									<div className="dark-text">Email:</div>
									<div className="light-text">
										{selectedBill?.customer_email &&
											selectedBill?.customer_email}
									</div>
								</div>

								{selectedBill?.buyer_pan && (
									<div className="text-section">
										<div className="dark-text">
											Customer VAT/PAN:
										</div>
										<div className="light-text">
											{selectedBill?.buyer_pan}
										</div>
									</div>
								)}
							</div>
							<div className="bill-container">
								<div className="text-container">
									<h5 className="section-title">
										Issued By:
									</h5>
									<div className="light-text capitalize">
										User ID #{" "}
										{selectedBill?.company_user &&
											selectedBill?.company_user.user
												?.user_id}
									</div>
								</div>
							</div>
							{selectedBill?.print_copies &&
							selectedBill.print_copies > 0 ? (
								<div className="bill-container">
									<div className="bill-flag-container">
										<div
											className="bill-flag"
											style={{
												backgroundColor: company_color_code,
											}}
										>
											Copy of Original &nbsp; [{" "}
											{selectedBill?.print_copies} ]
										</div>
									</div>
								</div>
							) : null}
						</div>
						{selectedBill?.bill_for && (
							<div className="header-text-section">
								<div className="dark-text">Bill For:</div>
								{_renderBillFor(selectedBill?.bill_for)}
							</div>
						)}
					</div>
				</div>

				<div className="template-body-container">
					<div
						className={`table-grid-container highlighted ${
							vatEnabled ? "six-col" : ""
						}`}
					>
						<div className="table-item-text">
							<b>S.N</b>
						</div>
						{vatEnabled && (
							<div className="table-item-text">
								<b>HS Code</b>
							</div>
						)}
						<div className="table-item-text">
							<b>Item Description</b>
						</div>
						<div className="table-item-text align-right">
							<b>Qty.</b>
						</div>
						<div className="table-item-text align-right">
							<b>Price</b>
						</div>
						<div className="table-item-text align-right">
							<b>Amount</b>
						</div>
					</div>
					{selectedBill?.sliced_items &&
					selectedBill.sliced_items.length > 0 ? (
						selectedBill.sliced_items.map((item, i) => (
							<div
								key={i}
								className={`table-grid-container ${
									vatEnabled ? "six-col" : ""
								}`}
							>
								<div className="table-item-text">
									{i + lastCount + 1}
								</div>
								{vatEnabled && (
									<div className="table-item-text">
										{item?.hs_code}
									</div>
								)}
								<div className="table-item-text capitalize">
									{item.item_name}
								</div>
								<div className="table-item-text align-right">
									{item.item_qty}
								</div>
								<div className="table-item-text align-right">
									{item.item_price}
								</div>
								<div className="table-item-text align-right">
									{item.total_price}
								</div>
							</div>
						))
					) : (
						<div style={{ minHeight: "25px" }} />
					)}
					{showBillFooter ? (
						<div className="table-footer-container">
							<div className="span-grid-item">
								<div style={{ flex: 1 }}>
									<div
										className="span-text highlighted"
										style={{
											color: company_color_code,
										}}
									>
										<b>
											{isCreditNote
												? "Reason for return"
												: "Additional Notes"}
										</b>
									</div>
									<div className="span-text ellipsis-text">
										{isCreditNote
											? selectedBill?.reason_for_return
											: selectedBill?.sale_info}
									</div>
								</div>
								<div className="payment-method-indicator">
									<div
										className="span-text highlighted"
										style={{
											color: company_color_code,
										}}
									>
										<b>Payment Method</b>
									</div>
									<div className="span-text capitalize">
										{selectedBill?.payment_mode}
									</div>
								</div>
							</div>
							<div className="table-item-text">
								<b>SubTotal</b>
							</div>
							<div className="table-item-text align-right">
								Rs. {rupeeConverter(selectedBill?.sub_total)}
							</div>
							<div className="table-item-text">
								<b>Discount</b>
							</div>
							<div className="table-item-text align-right">
								Rs.{" "}
								{rupeeConverter(
									(selectedBill?.taxable_discount || 0) +
										(selectedBill?.non_taxable_discount ||
											0),
								)}
							</div>
							{serviceChargeEnabled && (
								<>
									<div className="table-item-text">
										<b>Service Charge</b>
									</div>
									<div className="table-item-text align-right">
										Rs.{" "}
										{rupeeConverter(
											(selectedBill?.taxable_service_charge ||
												0) +
												(selectedBill?.non_taxable_service_charge ||
													0),
										)}
									</div>
								</>
							)}
							<div className="table-item-text">
								<b>Non-Taxable Amount</b>
							</div>
							<div className="table-item-text align-right">
								Rs.{" "}
								{rupeeConverter(
									selectedBill?.non_taxable_amt || 0,
								)}
							</div>
							<div className="table-item-text">
								<b>
									{esfEnabled
										? "ESF Applicable Amount"
										: "Taxable Amount"}
								</b>
							</div>
							<div className="table-item-text align-right">
								Rs.{" "}
								{rupeeConverter(
									(esfEnabled
										? selectedBill?.amount_for_esf
										: selectedBill?.taxable_sales_vat) || 0,
								)}
							</div>
							{vatEnabled && (
								<>
									<div className="table-item-text">
										<b>VAT @ 13%</b>
									</div>
									<div className="table-item-text align-right">
										Rs.{" "}
										{rupeeConverter(selectedBill?.vat || 0)}
									</div>
								</>
							)}
							{esfEnabled && (
								<>
									<div className="table-item-text">
										<b>ESF @ {esfRatePercent}%</b>
									</div>
									<div className="table-item-text align-right">
										Rs.{" "}
										{rupeeConverter(selectedBill?.esf || 0)}
									</div>
								</>
							)}
							<div
								className="table-item-text highlighted"
								style={{
									backgroundColor: company_color_code,
								}}
							>
								<b>Grand Total</b>
							</div>
							<div
								className="table-item-text highlighted align-right"
								style={{
									backgroundColor: company_color_code,
								}}
							>
								Rs. {rupeeConverter(selectedBill?.total_sales)}
							</div>
						</div>
					) : null}

					<div
						style={{
							borderBottom: "1px dashed #9D9D9D",
						}}
					/>
					{!showBillFooter ? (
						<div
							className="amount-item-indicator"
							style={{ justifyContent: "flex-end" }}
						>
							Number of Items:{" "}
							{selectedBill?.sliced_items?.length} out of{" "}
							{selectedBill?.billed_items?.length}
						</div>
					) : (
						<div className="amount-item-indicator">
							<span style={{ flex: 3, fontSize: "1em" }}>
								<b>Amount in words: </b>
								{TotalAmtInWords}
							</span>
							<span style={{ flex: 1, textAlign: "right" }}>
								Total Items:{" "}
								{selectedBill?.billed_items?.length}
							</span>
						</div>
					)}
				</div>

				<div className="template-footer-container">
					{isCreditNote || selectedBill?.bill_void ? (
						<div className="template-footer-row">
							<div className="footer-item">
								<div className="print-details">
									Print Date: {moment().format("llll")} |
									Employee: {session?.first_name}{" "}
									{session?.last_name}
								</div>
							</div>
						</div>
					) : (
						<div className="template-footer-row">
							<div className="footer-item">
								{showBillFooter && (
									<div className="footer-text">
										Thank you for your kind cooperation!
									</div>
								)}
								<div className="print-details">
									Print Date: {moment().format("llll")} |
									Employee: {session?.first_name}{" "}
									{session?.last_name}
								</div>
								{showBillFooter && (
									<>
										{termsCondition &&
											(selectedBill?.draft_id ||
												selectedBill?.print_copies ===
													0) && (
												<div className="footer-tnc">
													<b
														style={{
															color: company_color_code,
														}}
													>
														Terms & Conditions
													</b>
													<div>
														<div
															className="tnc-data"
															dangerouslySetInnerHTML={{
																__html: termsCondition,
															}}
														/>
													</div>
												</div>
											)}
									</>
								)}
							</div>

							{(selectedBill?.draft_id ||
								selectedBill?.print_copies === 0) && (
								<div className="footer-item flex-end">
									<div className="sign-space"></div>
									<div className="footer-text">
										Checked By
									</div>
								</div>
							)}
						</div>
					)}
					<div className="footer-divider"></div>
					<div className="brand-details-container">
						<div className="details-row">
							<div className="details-text">
								<b>Contact:</b>{" "}
								{company_phone && `${company_phone} | `}
								{company_mobile}
							</div>
							<div className="details-text">
								<b>E-mail:</b> {company_email}
							</div>
						</div>
						<div className="details-row">
							<div className="details-text capitalize">
								<b>Address:</b>{" "}
								{company_address && `${company_address},`}{" "}
								{company_city && `${company_city},`}{" "}
								{company_country}
							</div>
							{company_website && (
								<div className="details-text">
									<b>Web:</b> {company_website}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			{selectedBill?.bill_void ? (
				<div className="template-status void">void</div>
			) : null}

			{selectedBill?.bill_canceled ? (
				<div className="template-status void">canceled</div>
			) : null}
		</div>
	);
}
